import { Signal } from 'signal-polyfill';

import { adminApi } from '../../../api/admin.api';
import type {
  OrganizationCreateRequestDto,
  OrganizationDetailDto,
  OrganizationListItemDto,
  OrganizationUpdateRequestDto,
} from '../../../api/types';
import { navigate } from '../../../router';
import { authState, canAccessAppAdminConfiguration } from '../../../state/auth.state';
import { BaseElement } from '../../base-element';
import '../../ui/dt-button';
import '../../ui/dt-card';
import '../../ui/dt-dialog';
import '../../ui/dt-input';
import '../../ui/dt-spinner';
import './admin-config-shell';

type PageState = 'loading' | 'loaded' | 'empty' | 'error' | 'forbidden';
type OrganizationFormMode = 'create' | 'edit' | null;

interface OrganizationFormDraft {
  code: string;
  name: string;
  websiteUrl: string;
  addressLine1: string;
  addressLine2: string;
  addressCity: string;
  addressState: string;
  addressPostalCode: string;
  addressCountry: string;
  contactName: string;
  contactEmail: string;
}

type OrganizationField = keyof OrganizationFormDraft;

type DtDialogElement = HTMLElement & {
  showDialog(): void;
  closeDialog(): void;
};

type DtInputElement = HTMLElement & {
  value: string;
  error: string | null;
  validate(): boolean;
};

type DtButtonElement = HTMLElement & {
  loading?: boolean;
  disabled?: boolean;
};

const formFields: Array<{
  field: OrganizationField;
  label: string;
  type?: 'text' | 'email';
  required?: boolean;
  readOnlyOnEdit?: boolean;
}> = [
  { field: 'code', label: 'Code', required: true, readOnlyOnEdit: true },
  { field: 'name', label: 'Name', required: true },
  { field: 'websiteUrl', label: 'Website URL' },
  { field: 'contactName', label: 'Contact Name' },
  { field: 'contactEmail', label: 'Contact Email', type: 'email' },
  { field: 'addressLine1', label: 'Address Line 1' },
  { field: 'addressLine2', label: 'Address Line 2' },
  { field: 'addressCity', label: 'City' },
  { field: 'addressState', label: 'State' },
  { field: 'addressPostalCode', label: 'Postal Code' },
  { field: 'addressCountry', label: 'Country' },
];

function createEmptyDraft(): OrganizationFormDraft {
  return {
    code: '',
    name: '',
    websiteUrl: '',
    addressLine1: '',
    addressLine2: '',
    addressCity: '',
    addressState: '',
    addressPostalCode: '',
    addressCountry: '',
    contactName: '',
    contactEmail: '',
  };
}

function createDraftFromDetail(detail: OrganizationDetailDto): OrganizationFormDraft {
  return {
    code: detail.code ?? '',
    name: detail.name ?? '',
    websiteUrl: detail.websiteUrl ?? '',
    addressLine1: detail.addressLine1 ?? '',
    addressLine2: detail.addressLine2 ?? '',
    addressCity: detail.addressCity ?? '',
    addressState: detail.addressState ?? '',
    addressPostalCode: detail.addressPostalCode ?? '',
    addressCountry: detail.addressCountry ?? '',
    contactName: detail.contactName ?? '',
    contactEmail: detail.contactEmail ?? '',
  };
}

function optionalValue(value: string): string | null {
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function buildCreateRequest(draft: OrganizationFormDraft): OrganizationCreateRequestDto {
  return {
    code: draft.code.trim(),
    name: draft.name.trim(),
    logoBase64: null,
    logoMimeType: null,
    websiteUrl: optionalValue(draft.websiteUrl),
    addressLine1: optionalValue(draft.addressLine1),
    addressLine2: optionalValue(draft.addressLine2),
    addressCity: optionalValue(draft.addressCity),
    addressState: optionalValue(draft.addressState),
    addressPostalCode: optionalValue(draft.addressPostalCode),
    addressCountry: optionalValue(draft.addressCountry),
    contactName: optionalValue(draft.contactName),
    contactEmail: optionalValue(draft.contactEmail),
  };
}

function buildUpdateRequest(draft: OrganizationFormDraft): OrganizationUpdateRequestDto {
  return {
    name: draft.name.trim(),
    logoBase64: null,
    logoMimeType: null,
    websiteUrl: optionalValue(draft.websiteUrl),
    addressLine1: optionalValue(draft.addressLine1),
    addressLine2: optionalValue(draft.addressLine2),
    addressCity: optionalValue(draft.addressCity),
    addressState: optionalValue(draft.addressState),
    addressPostalCode: optionalValue(draft.addressPostalCode),
    addressCountry: optionalValue(draft.addressCountry),
    contactName: optionalValue(draft.contactName),
    contactEmail: optionalValue(draft.contactEmail),
  };
}

const validOrganizationFields = new Set<OrganizationField>([
  'code',
  'name',
  'websiteUrl',
  'addressLine1',
  'addressLine2',
  'addressCity',
  'addressState',
  'addressPostalCode',
  'addressCountry',
  'contactName',
  'contactEmail',
]);

function normalizeErrorField(fieldName: string): OrganizationField | null {
  const cleaned = fieldName
    .replace(/^\$\.?/, '')
    .replace(/^request\./i, '')
    .replace(/^organization\./i, '');
  const lastSegment = cleaned.split('.').pop() ?? cleaned;
  const normalized = `${lastSegment.charAt(0).toLowerCase()}${lastSegment.slice(1)}`;

  if (validOrganizationFields.has(normalized as OrganizationField)) {
    return normalized as OrganizationField;
  }

  return null;
}

export class AdminOrganizationsPage extends BaseElement {
  private organizations = new Signal.State<OrganizationListItemDto[]>([]);
  private loading = new Signal.State(false);
  private error = new Signal.State<string | null>(null);
  private forbidden = new Signal.State(false);
  private formMode = new Signal.State<OrganizationFormMode>(null);
  private formLoading = new Signal.State(false);
  private formSubmitting = new Signal.State(false);
  private formErrors = new Signal.State<Record<string, string[]>>({});
  private formMessage = new Signal.State<string | null>(null);
  private deleteTarget = new Signal.State<{ id: string; name: string } | null>(null);
  private deleteSubmitting = new Signal.State(false);
  private deleteConflictMessage = new Signal.State<string | null>(null);
  private initialized = false;

  private formDraft = createEmptyDraft();
  private editingOrganizationId: string | null = null;

  private pageState = new Signal.Computed<PageState>(() => {
    const auth = authState.get();

    if (!auth.isAuthenticated || !canAccessAppAdminConfiguration.get() || this.forbidden.get()) {
      return 'forbidden';
    }

    if (this.loading.get()) {
      return 'loading';
    }

    if (this.error.get()) {
      return 'error';
    }

    if (this.organizations.get().length === 0) {
      return 'empty';
    }

    return 'loaded';
  });

  connectedCallback(): void {
    this.watch(this.organizations, () => this.updateRender());
    this.watch(this.loading, () => this.updateRender());
    this.watch(this.error, () => this.updateRender());
    this.watch(this.forbidden, () => this.updateRender());
    this.watch(this.formMode, () => this.updateRender());
    this.watch(this.formLoading, () => this.updateRender());
    this.watch(this.formSubmitting, () => this.updateRender());
    this.watch(this.formErrors, () => this.updateRender());
    this.watch(this.formMessage, () => this.updateRender());
    this.watch(this.deleteTarget, () => this.updateRender());
    this.watch(this.deleteSubmitting, () => this.updateRender());
    this.watch(this.deleteConflictMessage, () => this.updateRender());
    this.watch(authState, () => this.ensureLoaded());
    this.watch(canAccessAppAdminConfiguration, () => this.ensureLoaded());

    this.updateRender();
    this.ensureLoaded();
  }

  private canAccess(): boolean {
    const auth = authState.get();
    return auth.isAuthenticated && canAccessAppAdminConfiguration.get();
  }

  private ensureLoaded(): void {
    if (!this.canAccess() || this.initialized) {
      return;
    }

    this.initialized = true;
    void this.loadOrganizations();
  }

  private async loadOrganizations(): Promise<void> {
    if (!this.canAccess()) {
      return;
    }

    this.loading.set(true);
    this.error.set(null);
    this.forbidden.set(false);

    const response = await adminApi.organizations.list();
    if (response.error) {
      this.organizations.set([]);
      this.loading.set(false);

      if (response.error.status === 401 || response.error.status === 403) {
        this.forbidden.set(true);
        return;
      }

      this.error.set(response.error.title || 'Unable to load organizations.');
      return;
    }

    this.organizations.set(response.data ?? []);
    this.loading.set(false);
  }

  private formatTimestamp(value?: string | null): string {
    if (!value) {
      return '—';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return parsed.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  private resetFormState(): void {
    this.formDraft = createEmptyDraft();
    this.editingOrganizationId = null;
    this.formErrors.set({});
    this.formMessage.set(null);
    this.formLoading.set(false);
    this.formSubmitting.set(false);
  }

  private openCreateDialog = (): void => {
    this.resetFormState();
    this.formMode.set('create');
  };

  private openEditDialog = async (organizationId: string): Promise<void> => {
    this.resetFormState();
    this.formMode.set('edit');
    this.formLoading.set(true);

    const response = await adminApi.organizations.getById(organizationId);
    this.formLoading.set(false);

    if (response.error) {
      if (response.error.status === 401 || response.error.status === 403) {
        this.forbidden.set(true);
        return;
      }

      this.formMessage.set(response.error.title || 'Unable to load organization details.');
      return;
    }

    if (!response.data) {
      this.formMessage.set('Unable to load organization details.');
      return;
    }

    this.editingOrganizationId = response.data.organizationId;
    this.formDraft = createDraftFromDetail(response.data);
    this.updateRender();
  };

  private closeFormDialog = (): void => {
    this.resetFormState();
    this.formMode.set(null);
  };

  private openDeleteDialog = (organizationId: string, organizationName: string): void => {
    this.deleteConflictMessage.set(null);
    this.deleteSubmitting.set(false);
    this.deleteTarget.set({ id: organizationId, name: organizationName });
  };

  private closeDeleteDialog = (): void => {
    this.deleteConflictMessage.set(null);
    this.deleteSubmitting.set(false);
    this.deleteTarget.set(null);
  };

  private handleRetry = (): void => {
    void this.loadOrganizations();
  };

  private handleManageSportCodes = (): void => {
    if (!this.editingOrganizationId) {
      return;
    }

    navigate(`/admin/configuration/organizations/${this.editingOrganizationId}/sport-codes`);
  };

  private handleFieldInput = (event: Event): void => {
    const target = event.currentTarget as HTMLElement;
    const field = target.getAttribute('data-field') as OrganizationField | null;
    const detail = (event as CustomEvent<{ value: string }>).detail;
    if (!field) {
      return;
    }

    this.formDraft = {
      ...this.formDraft,
      [field]: detail?.value ?? '',
    };

    const nextErrors = { ...this.formErrors.get() };
    delete nextErrors[field];
    this.formErrors.set(nextErrors);

    if (this.formMessage.get() === 'Please correct the highlighted fields and try again.') {
      this.formMessage.set(null);
    }
  };

  private validateForm(): boolean {
    const requiredFields: OrganizationField[] = ['name'];
    if (this.formMode.get() === 'create') {
      requiredFields.unshift('code');
    }

    return requiredFields.every((field) => {
      const input = this.$(`[data-field="${field}"]`) as DtInputElement | null;
      return input ? input.validate() : true;
    });
  }

  private applyServerErrors(errors?: Record<string, string[]>): void {
    if (!errors) {
      this.formErrors.set({});
      return;
    }

    const normalizedErrors: Record<string, string[]> = {};
    Object.entries(errors).forEach(([field, messages]) => {
      const normalizedField = normalizeErrorField(field);
      if (!normalizedField) {
        return;
      }

      normalizedErrors[normalizedField] = messages;
    });

    this.formErrors.set(normalizedErrors);
  }

  private async handleSave(): Promise<void> {
    if (!this.formMode.get()) {
      return;
    }

    this.formMessage.set(null);
    this.formErrors.set({});

    if (!this.validateForm()) {
      this.formMessage.set('Please correct the highlighted fields and try again.');
      return;
    }

    this.formSubmitting.set(true);

    const response = this.formMode.get() === 'create'
      ? await adminApi.organizations.create(buildCreateRequest(this.formDraft))
      : await adminApi.organizations.update(
          this.editingOrganizationId ?? '',
          buildUpdateRequest(this.formDraft)
        );

    this.formSubmitting.set(false);

    if (response.error) {
      if (response.error.status === 401 || response.error.status === 403) {
        this.forbidden.set(true);
        return;
      }

      this.applyServerErrors(response.error.errors);
      this.formMessage.set(
        response.error.errors
          ? 'Please correct the highlighted fields and try again.'
          : response.error.title || 'Unable to save organization.'
      );
      return;
    }

    this.closeFormDialog();
    await this.loadOrganizations();
  }

  private async handleDelete(): Promise<void> {
    const target = this.deleteTarget.get();
    if (!target) {
      return;
    }

    this.deleteConflictMessage.set(null);
    this.deleteSubmitting.set(true);

    const response = await adminApi.organizations.delete(target.id);

    this.deleteSubmitting.set(false);

    if (response.error) {
      if (response.error.status === 401 || response.error.status === 403) {
        this.forbidden.set(true);
        return;
      }

      if (response.error.status === 409 && response.error.errorCode === 'HAS_SPORT_CODES') {
        this.deleteConflictMessage.set(
          'This organization cannot be deleted while sport codes still exist. Remove the dependent sport codes first.'
        );
        return;
      }

      this.deleteConflictMessage.set(response.error.title || 'Unable to delete organization.');
      return;
    }

    this.closeDeleteDialog();
    await this.loadOrganizations();
  }

  private renderLoading(): string {
    return `
      <div class="flex min-h-[280px] flex-col items-center justify-center gap-3 rounded-[24px] border border-neutral-200 bg-white" data-testid="loading-state">
        <dt-spinner size="large"></dt-spinner>
        <p class="text-sm text-neutral-600">Loading organizations...</p>
      </div>
    `;
  }

  private renderError(message: string): string {
    return `
      <div class="rounded-[24px] border border-error-200 bg-error-50 p-6" data-testid="error-state">
        <h2 class="text-lg font-semibold text-error-700">Unable to load organizations</h2>
        <p class="mt-2 text-sm text-error-600">${this.esc(message)}</p>
        <dt-button class="mt-4" variant="outline" data-testid="retry-btn">Retry</dt-button>
      </div>
    `;
  }

  private renderEmpty(): string {
    return `
      <div class="rounded-[24px] border border-dashed border-neutral-300 bg-white p-10 text-center" data-testid="empty-state">
        <div class="text-lg font-semibold text-neutral-900">No organizations yet</div>
        <p class="mt-2 text-sm text-neutral-600">Create the first organization record to start the AppAdmin configuration catalog.</p>
        <dt-button class="mt-5" data-testid="create-empty-btn">Create organization</dt-button>
      </div>
    `;
  }

  private renderTable(): string {
    return `
      <dt-card variant="outlined">
        <div slot="header">Organizations</div>
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div class="max-w-2xl text-sm text-neutral-600">
              Organizations are the parent configuration records for later sport-code and form-template resources.
            </div>
            <dt-button data-testid="create-btn">Create organization</dt-button>
          </div>

          <div class="overflow-hidden rounded-[24px] border border-neutral-200 bg-white" data-testid="organizations-table">
            <div class="grid grid-cols-1 gap-3 border-b border-neutral-200 bg-neutral-50 px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-neutral-500 lg:grid-cols-[0.8fr_1.5fr_1.4fr_0.9fr_1fr] lg:gap-4">
              <div>Code</div>
              <div>Name</div>
              <div>Website</div>
              <div>Created</div>
              <div>Actions</div>
            </div>

            <div class="divide-y divide-neutral-200">
              ${this.organizations
                .get()
                .map(
                  (organization) => `
                    <div class="grid grid-cols-1 gap-4 px-4 py-4 lg:grid-cols-[0.8fr_1.5fr_1.4fr_0.9fr_1fr] lg:items-center" data-testid="organization-row" data-organization-id="${this.esc(organization.organizationId)}">
                      <div class="text-sm font-semibold text-neutral-900">${this.esc(organization.code)}</div>
                      <div class="text-sm text-neutral-700">${this.esc(organization.name)}</div>
                      <div class="text-sm text-neutral-700">
                        ${organization.websiteUrl ? `<a href="${this.esc(organization.websiteUrl)}" class="text-primary-600 underline" target="_blank" rel="noreferrer">${this.esc(organization.websiteUrl)}</a>` : '<span class="text-neutral-400">—</span>'}
                      </div>
                      <div class="text-sm text-neutral-600">${this.esc(this.formatTimestamp(organization.createdAtUtc))}</div>
                      <div class="flex flex-wrap items-center gap-2">
                        <button class="rounded-full border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-700 transition-colors hover:border-neutral-400 hover:bg-neutral-50" data-testid="edit-btn" data-organization-id="${this.esc(organization.organizationId)}">Edit</button>
                        <button class="rounded-full border border-rose-200 px-3 py-1.5 text-sm font-medium text-rose-700 transition-colors hover:bg-rose-50" data-testid="delete-btn" data-organization-id="${this.esc(organization.organizationId)}" data-organization-name="${this.esc(organization.name)}">Delete</button>
                      </div>
                    </div>
                  `,
                )
                .join('')}
            </div>
          </div>
        </div>
      </dt-card>
    `;
  }

  private renderFormDialog(): string {
    const mode = this.formMode.get();
    if (!mode) {
      return '';
    }

    const isCreate = mode === 'create';
    const title = isCreate ? 'Create organization' : 'Edit organization';

    return `
      <dt-dialog data-testid="organization-form-dialog">
        <div class="space-y-6">
          <div>
            <h2 class="text-2xl font-semibold text-neutral-950">${title}</h2>
            <p class="mt-2 text-sm text-neutral-600">Use the published organization DTO fields for shared application catalog data.</p>
          </div>

          ${
            this.formMessage.get()
              ? `<div class="rounded-2xl border border-error-200 bg-error-50 p-4 text-sm text-error-700" data-testid="form-message">${this.esc(this.formMessage.get() ?? '')}</div>`
              : ''
          }

          ${
            this.formLoading.get()
              ? `
                <div class="flex min-h-[220px] flex-col items-center justify-center gap-3">
                  <dt-spinner size="large"></dt-spinner>
                  <p class="text-sm text-neutral-600">Loading organization details...</p>
                </div>
              `
              : `
                <div class="grid gap-4 md:grid-cols-2">
                  ${formFields
                    .map(
                      (fieldConfig) => `
                        <dt-input
                          label="${fieldConfig.label}"
                          ${fieldConfig.type ? `type="${fieldConfig.type}"` : ''}
                          ${fieldConfig.required ? 'required' : ''}
                          ${!isCreate && fieldConfig.readOnlyOnEdit ? 'readonly' : ''}
                          data-field="${fieldConfig.field}"
                        ></dt-input>
                      `,
                    )
                    .join('')}
                </div>
              `
          }

          <div class="flex flex-wrap items-center justify-between gap-3">
            <div>
              ${
                !isCreate && this.editingOrganizationId
                  ? '<dt-button variant="outline" data-testid="manage-sport-codes-btn">Manage sport codes</dt-button>'
                  : ''
              }
            </div>
            <div class="flex flex-wrap items-center justify-end gap-3">
              <dt-button variant="ghost" data-testid="cancel-form-btn">Cancel</dt-button>
              <dt-button data-testid="save-form-btn">${isCreate ? 'Create organization' : 'Save changes'}</dt-button>
            </div>
          </div>
        </div>
      </dt-dialog>
    `;
  }

  private renderDeleteDialog(): string {
    const target = this.deleteTarget.get();
    if (!target) {
      return '';
    }

    return `
      <dt-dialog data-testid="delete-dialog">
        <div class="space-y-6">
          <div>
            <h2 class="text-2xl font-semibold text-neutral-950">Delete organization</h2>
            <p class="mt-2 text-sm text-neutral-600">This will remove <span class="font-semibold text-neutral-900">${this.esc(target.name)}</span> only if it has no dependent sport codes.</p>
          </div>

          ${
            this.deleteConflictMessage.get()
              ? `<div class="rounded-2xl border border-warning-200 bg-warning-50 p-4 text-sm text-warning-700" data-testid="delete-conflict-message">${this.esc(this.deleteConflictMessage.get() ?? '')}</div>`
              : ''
          }

          <div class="flex flex-wrap items-center justify-end gap-3">
            <dt-button variant="ghost" data-testid="cancel-delete-btn">Cancel</dt-button>
            <dt-button variant="outline" data-testid="confirm-delete-btn">Delete organization</dt-button>
          </div>
        </div>
      </dt-dialog>
    `;
  }

  private renderPageBody(): string {
    const state = this.pageState.get();
    if (state === 'loading') {
      return this.renderLoading();
    }

    if (state === 'error') {
      return this.renderError(this.error.get() ?? 'Unable to load organizations.');
    }

    if (state === 'empty') {
      return this.renderEmpty();
    }

    return this.renderTable();
  }

  private renderForbidden(): string {
    return `
      <section class="px-4 py-12" data-testid="forbidden-state">
        <div class="mx-auto max-w-xl text-center">
          <h2 class="text-xl font-semibold text-neutral-900">Access restricted</h2>
          <p class="mt-2 text-sm text-neutral-600">Only AppAdmin users can access the configuration area.</p>
        </div>
      </section>
    `;
  }

  private updateRender(): void {
    if (this.pageState.get() === 'forbidden') {
      this.render(this.renderForbidden());
      return;
    }

    this.render(`
      <dt-admin-config-shell
        active-section="organizations"
        section-title="Organizations"
        section-description="Manage the shared organization catalog, including parent records that later sport-code and form-template resources attach to."
        sport-codes-path="/admin/configuration/sport-codes"
      >
        ${this.renderPageBody()}
        ${this.renderFormDialog()}
        ${this.renderDeleteDialog()}
      </dt-admin-config-shell>
    `);

    this.attachEventListeners();
    this.assignData();
    this.syncDialogs();
  }

  private attachEventListeners(): void {
    const retryBtn = this.$('[data-testid="retry-btn"]');
    if (retryBtn) {
      retryBtn.addEventListener('click', this.handleRetry);
    }

    const createBtn = this.$('[data-testid="create-btn"]');
    if (createBtn) {
      createBtn.addEventListener('click', this.openCreateDialog);
    }

    const createEmptyBtn = this.$('[data-testid="create-empty-btn"]');
    if (createEmptyBtn) {
      createEmptyBtn.addEventListener('click', this.openCreateDialog);
    }

    this.$$('[data-testid="edit-btn"]').forEach((button) => {
      button.addEventListener('click', () => {
        const organizationId = button.getAttribute('data-organization-id');
        if (organizationId) {
          void this.openEditDialog(organizationId);
        }
      });
    });

    this.$$('[data-testid="delete-btn"]').forEach((button) => {
      button.addEventListener('click', () => {
        const organizationId = button.getAttribute('data-organization-id');
        const organizationName = button.getAttribute('data-organization-name') ?? 'this organization';
        if (organizationId) {
          this.openDeleteDialog(organizationId, organizationName);
        }
      });
    });

    const cancelFormBtn = this.$('[data-testid="cancel-form-btn"]');
    if (cancelFormBtn) {
      cancelFormBtn.addEventListener('click', this.closeFormDialog);
    }

    const saveFormBtn = this.$('[data-testid="save-form-btn"]');
    if (saveFormBtn) {
      saveFormBtn.addEventListener('click', () => {
        void this.handleSave();
      });
    }

    const manageSportCodesBtn = this.$('[data-testid="manage-sport-codes-btn"]');
    if (manageSportCodesBtn) {
      manageSportCodesBtn.addEventListener('click', this.handleManageSportCodes);
    }

    const cancelDeleteBtn = this.$('[data-testid="cancel-delete-btn"]');
    if (cancelDeleteBtn) {
      cancelDeleteBtn.addEventListener('click', this.closeDeleteDialog);
    }

    const confirmDeleteBtn = this.$('[data-testid="confirm-delete-btn"]');
    if (confirmDeleteBtn) {
      confirmDeleteBtn.addEventListener('click', () => {
        void this.handleDelete();
      });
    }

    this.$$('dt-input[data-field]').forEach((input) => {
      input.addEventListener('input', this.handleFieldInput);
    });

    const formDialog = this.$('[data-testid="organization-form-dialog"]');
    if (formDialog) {
      formDialog.addEventListener('close', this.closeFormDialog);
    }

    const deleteDialog = this.$('[data-testid="delete-dialog"]');
    if (deleteDialog) {
      deleteDialog.addEventListener('close', this.closeDeleteDialog);
    }
  }

  private assignData(): void {
    if (this.formMode.get()) {
      formFields.forEach((fieldConfig) => {
        const input = this.$(`[data-field="${fieldConfig.field}"]`) as DtInputElement | null;
        if (!input) {
          return;
        }

        input.value = this.formDraft[fieldConfig.field];
        input.error = this.formErrors.get()[fieldConfig.field]?.[0] ?? null;
      });

      const saveBtn = this.$('[data-testid="save-form-btn"]') as DtButtonElement | null;
      if (saveBtn) {
        saveBtn.loading = this.formSubmitting.get();
        saveBtn.disabled = this.formLoading.get() || this.formSubmitting.get();
      }
    }

    const confirmDeleteBtn = this.$('[data-testid="confirm-delete-btn"]') as DtButtonElement | null;
    if (confirmDeleteBtn) {
      confirmDeleteBtn.loading = this.deleteSubmitting.get();
      confirmDeleteBtn.disabled = this.deleteSubmitting.get();
    }
  }

  private syncDialogs(): void {
    const formDialog = this.$('[data-testid="organization-form-dialog"]') as DtDialogElement | null;
    if (formDialog) {
      formDialog.showDialog();
    }

    const deleteDialog = this.$('[data-testid="delete-dialog"]') as DtDialogElement | null;
    if (deleteDialog) {
      deleteDialog.showDialog();
    }
  }
}

customElements.define('dt-admin-organizations-page', AdminOrganizationsPage);